import type { LayoutDocument } from '../layout/types'
import { ApiError } from './api'
import { explain } from './staffErrors'
import { t, type StaffKey, type StaffLanguage } from './staffText'

// What the Designs editor says when the server refuses something to do with a
// playlist. Kept apart from signage.ts, which layout/editing.ts reads: the editor's
// rules are plain functions of a document, and should not bring the API client and
// every phrase table with them.

/**
 * Whether a save or a publish was refused because the design has a playlist and
 * the licence no longer covers one.
 *
 * Worth telling apart from any other refusal, because the way out is not obvious:
 * the playlist keeps playing on the wall (the serving path never asks the
 * licence), so the design looks fine, and yet nothing in it can be saved until the
 * playlist is taken off. The server's own sentence names the module and not that.
 */
export function refusedForSignage(err: unknown, doc: LayoutDocument | null): boolean {
  // The code as well as the status: LICENCE_LIMIT_REACHED is a 402 too, and a
  // design with a playlist refused because the licence is full must not be told
  // to take its playlist off.
  if (!(err instanceof ApiError) || err.status !== 402 || err.code !== 'MODULE_NOT_LICENSED' || !doc) return false
  return doc.elements.some((el) => el.kind === 'media')
}

/** The refusals an upload from the playlist panel can meet, each with its own words. */
const UPLOAD_REFUSALS: Record<string, StaffKey> = {
  UPLOAD_TOO_LARGE: 'designs.media.tooLarge',
  PICTURE_TOO_LARGE: 'designs.media.tooManyPixels',
  VIDEO_SPACE_FULL: 'designs.media.spaceFull',
  UPLOADS_BUSY: 'designs.media.uploadsBusy',
  MODULE_NOT_LICENSED: 'designs.media.filmUnlicensed',
}

/**
 * A refused upload, in the reader's language.
 *
 * explain() gives an Azerbaijani or Russian reader the server's English whenever a
 * code has no phrase, and the codes an upload meets are all English-only for the
 * owner's screens (lib/phrases/errors.ts). The playlist panel is the one place in
 * the editor that speaks the reader's language, so it keeps its own words for
 * those codes, and its own fallback for anything else. An English reader still
 * gets the server's sentence, which carries the figures (how much space is used).
 */
export function uploadRefusal(err: unknown, lang: StaffLanguage): string {
  const fallback: StaffKey = 'designs.media.uploadFailed'
  if (lang === 'en') return explain(err, lang, fallback)
  if (!(err instanceof ApiError)) return t(fallback, lang)
  if (err.code === 'VIDEO_SPACE_FULL' && err.reason === 'full') return t('designs.media.spaceFullDelete', lang)
  const own = UPLOAD_REFUSALS[err.code]
  if (own) return t(own, lang)
  const said = explain(err, lang, fallback)
  if (said !== err.message) return said
  return t(err.code === 'VALIDATION_FAILED' ? 'designs.media.notMedia' : fallback, lang)
}

/** Whether the film space, read again after a delete, now holds the film that was refused. */
export function roomFor(freeBytes: number | undefined, wantedBytes: number): boolean {
  return freeBytes !== undefined && freeBytes > 0 && freeBytes >= wantedBytes
}
